import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { RefreshCw, FileText, Users, Building2, CreditCard, AlertTriangle, TrendingUp, DollarSign } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ReportsTopBar from './ReportsTopBar';
import ReportFilters from './ReportFilters';
import { processReportData, exportReportCSV } from './reportUtils';
import SummaryTab from './tabs/SummaryTab';
import MemberUtilizationTab from './tabs/MemberUtilizationTab';
import HospitalAnalysisTab from './tabs/HospitalAnalysisTab';
import PolicyBalanceTab from './tabs/PolicyBalanceTab';
import HighCostTab from './tabs/HighCostTab';
import FraudTab from './tabs/FraudTab';
import MonthlyExposureTab from './tabs/MonthlyExposureTab';

const API = process.env.REACT_APP_BACKEND_URL;

const defaultFilters = () => ({
  dateFrom: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
  dateTo: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  hospital: 'all',
  member: '',
  policy: '',
  status: 'all',
  claimType: 'all',
  currency: 'USD'
});

const EMPTY_REPORT = {
  summary: {},
  memberUtilization: [],
  hospitalAnalysis: [],
  policyBalance: [],
  highCostClaims: [],
  fraudAlerts: [],
  monthlyExposure: []
};

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role');
  const hospitalName = localStorage.getItem('hospital_name');

  const canViewReports = username === 'superadmin';  // Reports Dashboard is for the superadmin user only

  const [filters, setFilters] = useState(defaultFilters);
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(true);
  const [hospitals, setHospitals] = useState([]);
  const [claims, setClaims] = useState([]);
  const [families, setFamilies] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!canViewReports) {
      toast.error('Access denied. Superadmin or ViewOnly access required.');
      navigate('/dashboard');
    }
  }, [token, canViewReports, navigate]);

  useEffect(() => {
    if (canViewReports) {
      loadInitialData();
    }
  }, [canViewReports]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [hospitalsRes, claimsRes, familiesRes] = await Promise.all([
        axios.get(`${API}/api/admin/hospitals`, axiosConfig),
        axios.get(`${API}/api/admin/claims/all?limit=10000`, axiosConfig),
        axios.get(`${API}/api/admin/families?limit=10000`, axiosConfig)
      ]);

      setHospitals(hospitalsRes.data || []);
      setClaims(claimsRes.data?.claims || claimsRes.data?.data || []);
      setFamilies(familiesRes.data?.data || familiesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const reportData = useMemo(
    () => (claims.length > 0 ? processReportData(claims, filters, families) : EMPTY_REPORT),
    [claims, filters, families]
  );

  const applyFilters = () => toast.success('Filters applied');

  const resetFilters = () => {
    setFilters(defaultFilters());
    toast.info('Filters reset');
  };

  const exportToCSV = () => exportReportCSV(activeTab, reportData);

  const exportToExcel = () => {
    exportToCSV();
    toast.info('Excel export uses CSV format');
  };

  const printReport = () => window.print();

  if (!canViewReports) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ReportsTopBar
        onExportCSV={exportToCSV}
        onExportExcel={exportToExcel}
        onPrint={printReport}
        onRefresh={loadInitialData}
        loading={loading}
      />

      <ReportFilters
        filters={filters}
        setFilters={setFilters}
        hospitals={hospitals}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
              <p className="text-gray-500">Loading report data...</p>
            </div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border shadow-sm p-1 flex-wrap h-auto gap-1">
              <TabsTrigger value="summary" className="gap-2 text-xs sm:text-sm">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Claims Summary</span>
                <span className="sm:hidden">Summary</span>
              </TabsTrigger>
              <TabsTrigger value="memberUtilization" className="gap-2 text-xs sm:text-sm">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Member Utilization</span>
                <span className="sm:hidden">Members</span>
              </TabsTrigger>
              <TabsTrigger value="hospitalAnalysis" className="gap-2 text-xs sm:text-sm">
                <Building2 className="w-4 h-4" />
                <span className="hidden sm:inline">Hospital Analysis</span>
                <span className="sm:hidden">Hospitals</span>
              </TabsTrigger>
              <TabsTrigger value="policyBalance" className="gap-2 text-xs sm:text-sm">
                <CreditCard className="w-4 h-4" />
                <span className="hidden sm:inline">Policy Balance</span>
                <span className="sm:hidden">Policies</span>
              </TabsTrigger>
              <TabsTrigger value="highCost" className="gap-2 text-xs sm:text-sm">
                <DollarSign className="w-4 h-4" />
                <span className="hidden sm:inline">High-Cost Claims</span>
                <span className="sm:hidden">High Cost</span>
              </TabsTrigger>
              <TabsTrigger value="fraud" className="gap-2 text-xs sm:text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span className="hidden sm:inline">Fraud Detection</span>
                <span className="sm:hidden">Fraud</span>
              </TabsTrigger>
              <TabsTrigger value="monthly" className="gap-2 text-xs sm:text-sm">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Monthly Exposure</span>
                <span className="sm:hidden">Monthly</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="summary">
              <SummaryTab summary={reportData.summary} />
            </TabsContent>
            <TabsContent value="memberUtilization">
              <MemberUtilizationTab memberUtilization={reportData.memberUtilization} />
            </TabsContent>
            <TabsContent value="hospitalAnalysis">
              <HospitalAnalysisTab hospitalAnalysis={reportData.hospitalAnalysis} />
            </TabsContent>
            <TabsContent value="policyBalance">
              <PolicyBalanceTab policyBalance={reportData.policyBalance} />
            </TabsContent>
            <TabsContent value="highCost">
              <HighCostTab highCostClaims={reportData.highCostClaims} />
            </TabsContent>
            <TabsContent value="fraud">
              <FraudTab fraudAlerts={reportData.fraudAlerts} />
            </TabsContent>
            <TabsContent value="monthly">
              <MonthlyExposureTab monthlyExposure={reportData.monthlyExposure} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { background: white; }
          .max-w-7xl { max-width: 100%; }
        }
      `}</style>
    </div>
  );
}
